import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Menu, MenuItem } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTheme, useMediaQuery } from '@mui/material';
import reasonicLogo from '../assets/reasonic-logo.svg';

const advertiserLinks = [
  { text: 'Dashboard', icon: <DashboardIcon />, href: '/home' },
  { text: 'Campaigns', icon: <CampaignIcon />, href: '/campaigns' },
  { text: 'Ad Strategist', icon: <AutoAwesomeIcon />, href: '/ad-strategist' },
  { text: 'Analytics', icon: <AnalyticsIcon />, href: '/analytics' },
];

const publisherLinks = [
  { text: 'Dashboard', icon: <DashboardIcon />, href: '/publisher-dashboard' },
  { text: 'Analytics', icon: <AnalyticsIcon />, href: '/publisher-analytics' },
  { text: 'Settings', icon: <SettingsIcon />, href: '/publisher-settings' },
];

const mainNavLinks = [
  { text: 'PUBLISHER', href: '/publishers', hasDropdown: true, dropdownItems: [
    { text: 'Overview', href: '/publishers' },
    { text: 'Documentation', href: '/documentation' },
    { text: 'API Reference', href: '/documentation#api' },
    { text: 'SDK Guide', href: '/documentation#sdk' },
  ]},
  { text: 'ADVERTISER', href: '/advertisers' },
  { text: 'PLAYGROUND', href: '/playground' },
  { text: 'SIGN UP', href: '/signup' },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [publisherMenuAnchor, setPublisherMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = () => {
    logout();
    navigate('/');
    handleUserMenuClose();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handlePublisherMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPublisherMenuAnchor(event.currentTarget);
  };

  const handlePublisherMenuClose = () => {
    setPublisherMenuAnchor(null);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Determine if sidebar should be shown
  const showSidebar = user && (user.user_type === 'advertiser' || user.user_type === 'publisher' || user.user_type === 'admin');
  
  // Determine which links to show based on user type
  const sidebarLinks = user?.user_type === 'publisher' ? publisherLinks : advertiserLinks;
  
  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer}>
      <ListItem component={RouterLink} to="/" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            component="img" 
            src={reasonicLogo} 
            alt="Reasonic Logo" 
            sx={{ 
              height: '30px', 
              width: 'auto',
              mr: 1
            }} 
          />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
            Reasonic
          </Typography>
        </Box>
      </ListItem>
      <Divider />
      <List>
        {mainNavLinks
          .filter(link => !user || (link.text !== 'LOGIN' && link.text !== 'SIGN UP'))
          .map((link) => (
          <ListItem 
            button 
            key={link.text} 
            component={link.hasDropdown ? 'div' : RouterLink} 
            to={link.hasDropdown ? undefined : link.href}
            onClick={link.text === 'PUBLISHER' ? handlePublisherMenuOpen : undefined}
          >
            <ListItemText primary={link.text} />
            {link.hasDropdown && <ArrowDropDownIcon />}
          </ListItem>
        ))}
      </List>
      {user && (
        <>
          <Divider />
          <List>
            <ListItem>
              <ListItemText 
                primary={user.name} 
                secondary={user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1)}
                primaryTypographyProps={{
                  fontWeight: 'bold',
                  color: '#3B82F6'
                }}
              />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
        color="transparent" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(180deg, #00051a 0%, #050d20 100%)',
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box 
            component={RouterLink} 
            to="/" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              px: 1.5,
              py: 0.8,
              borderRadius: 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <Box 
              component="img" 
              src={reasonicLogo} 
              alt="Reasonic Logo" 
              sx={{ 
                height: '38px', 
                width: 'auto',
                mr: 1.5,
                filter: 'drop-shadow(0 0 8px rgba(0, 119, 204, 0.5))'
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: 'white', 
                textDecoration: 'none',
                fontSize: '1.5rem',
                letterSpacing: '0.5px'
              }}
            >
              Reasonic
            </Typography>
          </Box>
          
          {isMobile ? (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
            >
              <MenuIcon sx={{ color: 'white' }} />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex' }}>
              {mainNavLinks
                .filter(link => !user || (link.text !== 'LOGIN' && link.text !== 'SIGN UP'))
                .map((link) => (
                  <React.Fragment key={link.text}>
                    {link.hasDropdown ? (
                      <>
                        <Button 
                          onClick={handlePublisherMenuOpen}
                          sx={{ 
                            mx: 1,
                            px: 2,
                            py: 0.8,
                            color: 'white',
                            fontWeight: '500',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '6px',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.05)',
                              color: '#60A5FA'
                            },
                          }}
                          endIcon={<ArrowDropDownIcon />}
                        >
                          {link.text}
                        </Button>
                        <Menu
                          anchorEl={publisherMenuAnchor}
                          open={Boolean(publisherMenuAnchor)}
                          onClose={handlePublisherMenuClose}
                          PaperProps={{
                            elevation: 3,
                            sx: {
                              minWidth: 180,
                              mt: 1,
                              backgroundColor: '#1E293B',
                              color: 'white',
                              '& .MuiMenuItem-root:hover': {
                                backgroundColor: 'rgba(59, 130, 246, 0.15)'
                              }
                            }
                          }}
                        >
                          {link.dropdownItems?.map((item) => (
                            <MenuItem 
                              key={item.text} 
                              onClick={() => {
                                navigate(item.href);
                                handlePublisherMenuClose();
                              }}
                            >
                              {item.text}
                            </MenuItem>
                          ))}
                          {user?.user_type === 'publisher' && (
                            <MenuItem 
                              onClick={() => {
                                navigate('/publisher-dashboard');
                                handlePublisherMenuClose();
                              }}
                            >
                              Publisher Dashboard
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    ) : (
                      <Button 
                        component={RouterLink}
                        to={link.href}
                        sx={{ 
                          mx: 1,
                          px: 2,
                          py: 0.8,
                          color: 'white',
                          fontWeight: '500',
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: '6px',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: '#60A5FA'
                          },
                          '&::after': location.pathname === link.href ? {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '30%',
                            height: '3px',
                            backgroundColor: '#3B82F6',
                            borderRadius: '3px 3px 0 0'
                          } : {}
                        }}
                      >
                        {link.text}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              
              {user ? (
                <>
                  <Button 
                    onClick={handleUserMenuOpen}
                    sx={{ 
                      ml: 2,
                      color: 'white',
                      backgroundColor: 'rgba(59, 130, 246, 0.15)',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.25)',
                      }
                    }}
                    startIcon={<AccountCircleIcon />}
                    endIcon={<ArrowDropDownIcon />}
                  >
                    {user.name}
                  </Button>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        minWidth: 180,
                        mt: 1,
                        backgroundColor: '#1E293B',
                        color: 'white',
                        '& .MuiMenuItem-root:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.15)'
                        }
                      }
                    }}
                  >
                    <MenuItem>
                      <Box sx={{ py: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {user.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                          {user.email}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          display: 'inline-block',
                          mt: 1,
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          color: '#60A5FA'
                        }}>
                          {user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1)}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    {user.user_type === 'publisher' && (
                      <MenuItem 
                        onClick={() => {
                          navigate('/publisher-dashboard');
                          handleUserMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <DashboardIcon sx={{ color: '#94A3B8' }} />
                        </ListItemIcon>
                        Publisher Dashboard
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon sx={{ color: '#94A3B8' }} />
                      </ListItemIcon>
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button 
                    component={RouterLink}
                    to="/login"
                    sx={{ 
                      ml: 2,
                      px: 3,
                      py: 0.8,
                      color: 'white',
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                      borderRadius: '6px',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.05)'
                      }
                    }}
                    startIcon={<LoginIcon />}
                  >
                    Login
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            backgroundColor: '#1E293B',
            color: 'white',
            width: 250
          }
        }}
      >
        {drawer}
      </Drawer>
      
      <Box sx={{ display: 'flex', flex: 1 }}>
        {showSidebar && !isMobile && !isLandingPage && (
          <Box
            sx={{
              width: 240,
              flexShrink: 0,
              backgroundColor: '#0F172A',
              borderRight: '1px solid rgba(59, 130, 246, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              pt: 3
            }}
          >
            <List>
              {sidebarLinks.map((link) => (
                <ListItem 
                  button 
                  key={link.text}
                  component={RouterLink}
                  to={link.href}
                  sx={{
                    mb: 0.5,
                    mx: 1.5,
                    borderRadius: '8px',
                    color: location.pathname === link.href ? '#FFFFFF' : '#94A3B8',
                    backgroundColor: location.pathname === link.href ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                    '&:hover': {
                      backgroundColor: location.pathname === link.href ? 'rgba(59, 130, 246, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: '40px',
                    color: location.pathname === link.href ? '#3B82F6' : '#64748B'
                  }}>
                    {link.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={link.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === link.href ? '600' : '400'
                    }} 
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1,
            backgroundColor: '#0B1120',
            color: 'white',
            minHeight: '100%'
          }}
        >
          {children}
        </Box>
      </Box>
      
      {!isLandingPage && (
        <Box component="footer" sx={{ py: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Reasonic - LLM Ad Integration Platform
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Layout; 